import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useProfileContext } from '@/features/context/ProfileContext';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { selectProfileByUserId } from '@/store/selectors';
import { getBeneficiaries } from '@/store/actions/profile.actions';

import {
  IBeneficiary,
  IFilterBeneficiary,
  TMetaBeneficiary,
} from '@/features/beneficiaries/store/models/beneficiary.model';
import {
  IAllBeneficiariesFiltersFormFields,
  parseAllBeneficiariesFiltersValue,
} from '@/features/beneficiaries/hooks/AllBeneficiaries/useAllBeneficiariesFilter';
import { useAccesControl } from '@/hooks/useAccessControl';
import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { parseAllParameters } from '@/ui-kit/table-filters/filters.type';

export type TBeneficiaryData = {
  beneficiaries: IBeneficiary[];
  meta: TMetaBeneficiary;
  isLoading: boolean;
  filter?: IAllBeneficiariesFiltersFormFields;
};

export type TBeneficiaryAction = 'view' | 'edit' | 'redirect';

export function useUserBeneficiariesData() {
  const { user } = useProfileContext();
  const dispatch = useAppDispatch();

  const { hasAccessToFeature } = useAccesControl();

  const [searchParams] = useSearchParams();

  const metaBeneficiaries = useAppSelector(
    (state) => selectProfileByUserId(state, user.userId)?.state?.beneficiaries?.meta,
  );

  const beneficiaries = useAppSelector((state) =>
    selectProfileByUserId(state, user.userId)?.state?.beneficiaries?.state?.map(
      (beneficiaryState) => beneficiaryState.state?.beneficiary,
    ),
  );

  const isLoading = useAppSelector((state) =>
    Boolean(selectProfileByUserId(state, user.userId)?.state?.beneficiaries?.isLoading),
  );

  const filterProps = useMemo(() => {
    if (!searchParams.get('beneficiaryId')) return {};

    return {
      type: searchParams.get('type'),
      search: {
        key: 'id',
        value: searchParams.get('beneficiaryId'),
      },
    };
  }, [searchParams]);

  const getUserBeneficiaries = useCallback(
    (filter: IFilterBeneficiary, hasInitialProps: boolean) => {
      dispatch(
        getBeneficiaries(user.userId, {
          ...(hasInitialProps
            ? parseAllParameters<IAllBeneficiariesFiltersFormFields, IFilterBeneficiary>(
                filterProps,
                parseAllBeneficiariesFiltersValue,
              )
            : {}),
          ...filter,
        }),
      );
    },
    [dispatch, filterProps, user.userId],
  );

  const actions = [
    ...(hasAccessToFeature(featuresSlugs.EDIT_BENEFICIARY) ? ['edit'] : []),
    ...(hasAccessToFeature(featuresSlugs.ALL_BENEFICIARIES) ? ['view'] : []),
  ] as TBeneficiaryAction[];

  return {
    data: {
      beneficiaries,
      meta: metaBeneficiaries,
      isLoading,
      filter: filterProps,
    },
    getData: getUserBeneficiaries,
    actions,
  };
}
